import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import PanelLayout from "@/components/layout/PanelLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Send, Trash2, Copy, Check, Mail, FileSpreadsheet, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AdSlotBanner } from "@/components/referrals/AdSlotBanner";

const NEW_ROW = () => ({
  target_email: "",
  target_profile_hint: "",
  target_country_hint: "",
  target_language_hint: "",
  personal_note: "",
});

const statusColor = (s) => ({
  queued: "bg-slate-100 text-slate-700",
  sent: "bg-sky-100 text-sky-800",
  delivered: "bg-sky-100 text-sky-800",
  opened: "bg-violet-100 text-violet-800",
  clicked: "bg-violet-100 text-violet-800",
  registered: "bg-emerald-100 text-emerald-800",
  verified: "bg-emerald-200 text-emerald-900",
  submitted: "bg-emerald-600 text-white",
  bounced: "bg-amber-100 text-amber-900",
  expired: "bg-slate-200 text-slate-600",
  revoked: "bg-rose-100 text-rose-800",
}[s] || "bg-slate-100 text-slate-700");

/**
 * A19-P1.3 — Authenticated "Invite peers" dashboard.
 * Path: /app/referrals
 *
 * Sections:
 *   1) Invite composer (up to 5 rows)
 *   2) Funnel mini-card (sent / opened / clicked / registered / verified / submitted)
 *   3) My referrals table with copyable referral URL and status badge
 */
const MyReferrals = () => {
  const { user } = useAuth();
  const { t, lang, tCountry } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([NEW_ROW()]);
  const [sending, setSending] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [list, setList] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  // A19-P1.3 — country catalog used by the per-row country dropdown so
  // the user picks a valid ISO code from a localized, alphabetized
  // list rather than free-typing a 2-letter code that could land as
  // garbage in the referral_invite payload.
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  }, []);
  // Sort alphabetically by the user's current locale, mirroring the
  // pattern used in AdminTesters.jsx and the Register flow.
  const sortedCountries = React.useMemo(() => {
    const collator = new Intl.Collator(lang || "en", { sensitivity: "base" });
    return [...countries].sort((a, b) => {
      const an = tCountry ? tCountry(a.code, a) : (a.name_en || a.code);
      const bn = tCountry ? tCountry(b.code, b) : (b.name_en || b.code);
      return collator.compare(an, bn);
    });
  }, [countries, lang, tCountry]);
  const activeLanguages = React.useMemo(
    () => LANGUAGES.filter((Lg) => Lg.isActive),
    [],
  );
  // A19-P2 — Bulk import state.
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkDryRun, setBulkDryRun] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileInputRef = React.useRef(null);
  // A19-P5.1 — active campaign boost chip.
  const [boost, setBoost] = useState(null);
  // A19-P5.2.2 — active boost progress (extras earned + days remaining).
  const [boostProgress, setBoostProgress] = useState(null);
  // A19-P6.1 — Ad-slot lite: highest-multiplier campaign matching this user.
  const [adSlot, setAdSlot] = useState(null);
  // A19-P6.2 — Recently-completed boost celebrations (caller-scoped).
  const [completedBoosts, setCompletedBoosts] = useState([]);

  // D2-cosmetic — Stable list of profile options offered in the "Their
  // profile" dropdown of the invite composer. The `value` matches the
  // ROLE_OR_DOMAIN sent to the backend's referral_invite payload (so
  // legacy data stays consistent); the `labelKey` resolves via
  // `t("inviteProfileOptions.<key>")` to honour the active language.
  const PROFILE_OPTIONS = React.useMemo(() => [
    { value: "player", labelKey: "player" },
    { value: "parent", labelKey: "parent" },
    { value: "coach", labelKey: "coach" },
    { value: "head_coach", labelKey: "headCoach" },
    { value: "club", labelKey: "club" },
    { value: "federation", labelKey: "federation" },
    { value: "physical_trainer", labelKey: "physicalTrainer" },
    { value: "physio", labelKey: "physio" },
    { value: "stringer", labelKey: "stringer" },
    { value: "sponsor", labelKey: "sponsor" },
    { value: "other", labelKey: "other" },
  ], []);

  const fetchAll = async () => {
    const [a, b, c, d, e, f] = await Promise.all([
      api.get("/referrals/my/funnel").then((r) => r.data).catch(() => null),
      api.get("/referrals/my").then((r) => r.data.invitations || []).catch(() => []),
      api.get("/referrals/active-boost").then((r) => r.data).catch(() => null),
      api.get("/referrals/active-boost/progress").then((r) => r.data).catch(() => null),
      api.get("/referrals/active-boost-for-me").then((r) => r.data).catch(() => null),
      api.get("/referrals/recent-completed-boosts").then((r) => r.data?.boosts || []).catch(() => []),
    ]);
    setFunnel(a);
    setList(b);
    setBoost(c);
    setBoostProgress(d);
    setAdSlot(e);
    // Filter out any boosts the user already dismissed locally.
    try {
      const filtered = (f || []).filter((b2) =>
        !localStorage.getItem(`dismissed_completed_boost_${b2.campaign_id}`)
      );
      setCompletedBoosts(filtered);
    } catch { setCompletedBoosts(f || []); }
  };

  useEffect(() => { fetchAll(); }, []);

  // A19-P6.1 — scroll-and-focus the invite composer when the user clicks the CTA.
  // A19-P6.2 — dismiss a completed-boost celebration card.
  const dismissCompletedBoost = (campaignId) => {
    try { localStorage.setItem(`dismissed_completed_boost_${campaignId}`, "1"); } catch { /* no-op */ }
    setCompletedBoosts((prev) => prev.filter((b) => b.campaign_id !== campaignId));
  };

  const focusInviteComposer = () => {
    const el = document.querySelector('[data-testid="invite-composer-card"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      const firstEmail = document.querySelector('[data-testid="invite-composer-email-0"]');
      if (firstEmail) setTimeout(() => firstEmail.focus(), 350);
    }
  };

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const addRow = () => setRows((rs) => (rs.length >= 5 ? rs : [...rs, NEW_ROW()]));

  const submit = async () => {
    const targets = rows
      .map((r) => ({
        target_email: r.target_email.trim(),
        target_profile_hint: r.target_profile_hint || null,
        target_country_hint: r.target_country_hint || null,
        target_language_hint: r.target_language_hint || null,
        personal_note: r.personal_note || null,
      }))
      .filter((r) => r.target_email);
    if (!targets.length) return;
    setSending(true);
    try {
      const res = await api.post("/referrals/invite", { targets });
      const n = res.data?.invitations?.length || 0;
      toast.success(n === 1 ? t("referrals.composer.successOne") : t("referrals.composer.successMany", { n }));
      setRows([NEW_ROW()]);
      await fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("referrals.composer.submit")));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800);
    } catch {
      toast.error("copy_failed");
    }
  };

  // A19-P1.4 — download the reference Excel template (preview of P2 bulk import).
  // BUGFIX (CA/EU/GL smoke, Feb-2026): when the backend returns a JSON error
  // (e.g. demo middleware 423, or auth 401) on a responseType:"blob" request,
  // axios delivers err.response.data as a Blob, not as the parsed JSON object.
  // extractErrorMessage then can't see `.detail` and falls back to the raw
  // "Request failed with status code 423" string. We unwrap the Blob into
  // JSON before handing the error to the standard formatter — and we cover
  // 423 explicitly so the toast reads as a friendly "not available in demo".
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/referrals/bulk-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "teamsportworld_bulk_invite_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Unwrap Blob-shaped error payload into the JSON the formatter expects.
      const blob = err?.response?.data;
      if (blob && typeof blob.text === "function") {
        try {
          const raw = await blob.text();
          err.response.data = raw ? JSON.parse(raw) : {};
        } catch {
          // Non-JSON blob (e.g. truly the xlsx bytes on an unrelated failure)
          // — leave .data as-is; extractErrorMessage will status-fallback.
        }
      }
      toast.error(extractErrorMessage(err, t("referrals.errors.template_download_failed") || "Could not download the template."));
    }
  };

  // A19-P2 — upload an .xlsx for bulk import.
  const handleBulkFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error(t("referrals.errors.bulk_invalid_file_type") || "Only .xlsx files are accepted.");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const res = await api.post(`/referrals/bulk-import?dry_run=${bulkDryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(res.data);
      if (!bulkDryRun) await fetchAll();
      toast.success(
        bulkDryRun
          ? t("referrals.bulk.dryRunSuccess", { a: res.data.accepted, r: res.data.rejected })
          : t("referrals.bulk.liveSuccess", { a: res.data.accepted, r: res.data.rejected })
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "bulk_failed"));
    } finally {
      setBulkBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadReport = () => {
    if (!bulkResult?.report_csv_b64) return;
    const bytes = Uint8Array.from(atob(bulkResult.report_csv_b64), (c) => c.charCodeAt(0));
    const url = window.URL.createObjectURL(new Blob([bytes], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "teamsportworld_bulk_report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PanelLayout>
      <div className="space-y-8" data-testid="my-referrals-page">
        <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="my-referrals-title">
              {t("referrals.title")}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">{t("referrals.subtitle")}</p>
          </div>
          {/* A19-P1.4 — Excel template download for future P2 bulk-import. */}
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="self-start sm:self-auto border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            data-testid="my-referrals-download-template-btn"
            title={t("referrals.bulk.downloadTemplateHint")}
          >
            <FileSpreadsheet className="w-4 h-4 me-2" />
            {t("referrals.bulk.downloadTemplate")}
          </Button>
        </div>

        {/* A19-P5.1 — Active campaign boost chip (read-only, hidden when no campaign is live). */}
        {boost?.active && (
          <div
            className="rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-50 to-emerald-100 px-4 py-3 flex items-start sm:items-center gap-3 flex-col sm:flex-row"
            data-testid="referrals-active-boost-banner"
          >
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3 py-1 text-xs font-semibold tracking-wide shadow-sm"
              data-testid="referrals-active-boost-chip"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t("referrals.boost.chip", { m: boost.multiplier })}
            </span>
            <p className="text-sm text-emerald-900 leading-snug" data-testid="referrals-active-boost-text">
              {(() => {
                const endDate = (() => {
                  try {
                    return new Intl.DateTimeFormat(lang === "pt-br" ? "pt-BR" : lang, {
                      day: "2-digit", month: "short",
                    }).format(new Date(boost.ends_at));
                  } catch { return boost.ends_at?.slice(0, 10); }
                })();
                const profileLabel = boost.target_profile
                  ? (t(`profiles.${boost.target_profile}`) || boost.target_profile)
                  : null;
                const main = profileLabel
                  ? t("referrals.boost.invite", { profile: profileLabel, date: endDate })
                  : t("referrals.boost.inviteAny", { date: endDate });
                const countrySuffix = boost.target_country
                  ? t("referrals.boost.countrySuffix", { country: boost.target_country })
                  : "";
                return main + countrySuffix;
              })()}
            </p>
          </div>
        )}

        {/* A19-P5.2.2 — Personal boost progress (extras earned during the live campaign). */}
        {boostProgress?.active && (
          <div
            className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm"
            data-testid="referrals-boost-progress-card"
          >
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div className="flex items-baseline gap-2">
                <span
                  className="text-2xl font-bold text-emerald-700 tabular-nums"
                  data-testid="referrals-boost-progress-extra"
                >
                  +{boostProgress.extra_points}
                </span>
                <span className="text-sm text-slate-600">
                  {t("referrals.boost.progressExtra")}
                </span>
              </div>
              <span
                className="text-sm font-medium text-emerald-700"
                data-testid="referrals-boost-progress-days"
              >
                {t("referrals.boost.progressDays", { days: boostProgress.days_remaining })}
              </span>
            </div>
            {/* Simple visual progress bar: portion of multiplier that has been "captured" so far,
                clamped 0..1. Pure CSS, no extra deps. */}
            <div className="mt-3 h-1.5 w-full rounded-full bg-emerald-100 overflow-hidden" aria-hidden="true">
              <div
                className="h-full bg-emerald-500 transition-all"
                data-testid="referrals-boost-progress-bar"
                style={{
                  width: (() => {
                    const target = Math.max(50, (boostProgress.base_points || 0) * 2);
                    const ratio = Math.min(1, (boostProgress.extra_points || 0) / target);
                    return `${Math.max(4, Math.round(ratio * 100))}%`;
                  })(),
                }}
              />
            </div>
          </div>
        )}

        {/* A19-P6.2 — Recently-completed boost celebration cards (caller-scoped). */}
        {completedBoosts.length > 0 && (
          <div className="space-y-3" data-testid="referrals-celebrations">
            {completedBoosts.map((b) => (
              <div
                key={b.campaign_id}
                className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-pink-50 to-white p-5 shadow-sm relative"
                data-testid={`referrals-celebration-${b.campaign_id}`}
              >
                <button
                  type="button"
                  onClick={() => dismissCompletedBoost(b.campaign_id)}
                  className="absolute top-2 right-3 text-purple-400 hover:text-purple-700 text-lg leading-none"
                  data-testid={`referrals-celebration-dismiss-${b.campaign_id}`}
                  aria-label={t("referrals.celebration.dismiss")}
                  title={t("referrals.celebration.dismiss")}
                >
                  ×
                </button>
                <div className="flex items-start gap-3">
                  <span className="text-3xl select-none" aria-hidden="true">🎉</span>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-purple-900" data-testid="referrals-celebration-title">
                      {t("referrals.celebration.title", { campaign: b.campaign_name })}
                    </h3>
                    <p className="text-sm text-purple-800 mt-1" data-testid="referrals-celebration-extras">
                      {b.extra_points > 0
                        ? t("referrals.celebration.extras", { points: b.extra_points })
                        : t("referrals.celebration.thanks")}
                    </p>
                    {b.next_boost_in_days != null && (
                      <p className="text-xs text-purple-700/80 mt-1.5" data-testid="referrals-celebration-next">
                        {b.next_boost_in_days === 0
                          ? t("referrals.celebration.nextNow")
                          : t("referrals.celebration.next", { days: b.next_boost_in_days })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* A19-P6.3 — Internal promotional slot for placement `referrals_top`.
            Renders only when the engine surfaces a matching slot for this user. */}
        <AdSlotBanner placement="referrals_top" onComposerCta={focusInviteComposer} />

        {/* Funnel */}
        {funnel && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="referrals-funnel">
            {["sent","opened","clicked","registered","verified","submitted"].map((k) => (
              <Card key={k} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="text-xs text-slate-500 uppercase">{t(`referrals.funnel.${k}`)}</div>
                  <div className="text-2xl font-bold text-emerald-700" data-testid={`referrals-funnel-${k}`}>{funnel[k] ?? 0}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* A19-P6.1 — In-app promo banner for a boost matching this user. */}
        {adSlot?.active && (
          <div
            className="relative overflow-hidden rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 via-amber-50 to-white p-5 shadow-sm"
            data-testid="referrals-ad-slot-banner"
          >
            <div className="absolute inset-y-0 left-0 w-1 bg-amber-500" aria-hidden="true" />
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                    {t("referrals.adSlot.tag")}
                  </span>
                  <span className="text-xs text-slate-500">
                    {t("referrals.adSlot.internalNotice")}
                  </span>
                </div>
                <p className="text-base font-semibold text-slate-900" data-testid="referrals-ad-slot-text">
                  {(() => {
                    const m = `${adSlot.multiplier}`.replace(/\.0$/, "");
                    const endDate = (() => {
                      try {
                        return new Intl.DateTimeFormat(lang === "pt-br" ? "pt-BR" : lang, {
                          day: "2-digit", month: "short",
                        }).format(new Date(adSlot.ends_at));
                      } catch { return (adSlot.ends_at || "").slice(0, 10); }
                    })();
                    const profileLabel = adSlot.target_profile
                      ? (t(`profiles.${adSlot.target_profile}`) || adSlot.target_profile)
                      : null;
                    return profileLabel
                      ? t("referrals.adSlot.bodyProfile", { m, profile: profileLabel, date: endDate })
                      : t("referrals.adSlot.bodyAny", { m, date: endDate });
                  })()}
                </p>
              </div>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                onClick={focusInviteComposer}
                data-testid="referrals-ad-slot-cta"
              >
                {t("referrals.adSlot.cta")}
              </Button>
            </div>
          </div>
        )}

        {/* A19-P2 — Bulk import (.xlsx) */}
        <Card data-testid="bulk-import-card" className="border-emerald-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="w-4 h-4 text-emerald-700" />
              {t("referrals.bulk.heading")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">{t("referrals.bulk.intro")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm" data-testid="bulk-import-dry-run-toggle">
                <input
                  type="checkbox"
                  checked={bulkDryRun}
                  onChange={(e) => setBulkDryRun(e.target.checked)}
                />
                {t("referrals.bulk.dryRunLabel")}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleBulkFile}
                data-testid="bulk-import-file-input"
              />
              <Button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={bulkBusy}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="bulk-import-upload-btn"
              >
                <Upload className="w-4 h-4 me-2" />
                {bulkBusy ? t("referrals.bulk.uploading") : t("referrals.bulk.uploadBtn")}
              </Button>
              {bulkResult && (
                <Button
                  variant="outline"
                  onClick={downloadReport}
                  data-testid="bulk-import-download-report-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 me-2" />
                  {t("referrals.bulk.downloadReport")}
                </Button>
              )}
            </div>
            {bulkResult && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid="bulk-import-result">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="bulk-import-result-accepted">
                  <div className="text-xs text-emerald-700 uppercase">{t("referrals.bulk.accepted")}</div>
                  <div className="text-2xl font-bold text-emerald-800">{bulkResult.accepted}</div>
                </div>
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="bulk-import-result-rejected">
                  <div className="text-xs text-rose-700 uppercase">{t("referrals.bulk.rejected")}</div>
                  <div className="text-2xl font-bold text-rose-800">{bulkResult.rejected}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-600 uppercase">{t("referrals.bulk.mode")}</div>
                  <div className="text-sm font-medium">
                    {bulkResult.dry_run ? t("referrals.bulk.modeDry") : t("referrals.bulk.modeLive")}
                  </div>
                </div>
              </div>
            )}
            <p className="text-xs text-slate-500">{t("referrals.bulk.hint")}</p>
          </CardContent>
        </Card>

        {/* Composer */}
        <Card data-testid="invite-composer-card">
          <CardHeader>
            <CardTitle>{t("referrals.composer.heading")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {rows.map((r, i) => (
              <div key={i} className="grid sm:grid-cols-12 gap-2 items-start border border-slate-200 rounded-lg p-3" data-testid={`invite-composer-row-${i}`}>
                <input
                  type="email"
                  placeholder={t("referrals.composer.emailLabel")}
                  value={r.target_email}
                  onChange={(e) => updateRow(i, { target_email: e.target.value })}
                  className="sm:col-span-5 tsw-input"
                  data-testid={`invite-composer-email-${i}`}
                />
                <select
                  value={r.target_profile_hint || ""}
                  onChange={(e) => updateRow(i, { target_profile_hint: e.target.value })}
                  className="sm:col-span-3 tsw-input"
                  data-testid={`invite-composer-profile-${i}`}
                >
                  <option value="">{t("inviteProfileOptions.placeholder") || t("referrals.composer.profileLabel")}</option>
                  {PROFILE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(`inviteProfileOptions.${opt.labelKey}`)}
                    </option>
                  ))}
                </select>
                <select
                  value={r.target_country_hint || ""}
                  onChange={(e) => updateRow(i, { target_country_hint: e.target.value })}
                  className="sm:col-span-2 tsw-input"
                  data-testid={`invite-composer-country-${i}`}
                >
                  <option value="">{t("referrals.composer.countryLabel")}</option>
                  {sortedCountries.map((c) => (
                    <option key={c.code} value={c.code}>{tCountry ? tCountry(c.code, c) : (c.name_en || c.code)}</option>
                  ))}
                </select>
                <select
                  value={r.target_language_hint || ""}
                  onChange={(e) => updateRow(i, { target_language_hint: e.target.value })}
                  className="sm:col-span-2 tsw-input"
                  data-testid={`invite-composer-lang-${i}`}
                >
                  <option value="">{t("referrals.composer.languageLabel")}</option>
                  {activeLanguages.map((Lg) => (
                    <option key={Lg.code} value={Lg.code}>{Lg.nameNative}</option>
                  ))}
                </select>
                <Textarea
                  placeholder={t("referrals.composer.noteLabel")}
                  value={r.personal_note}
                  onChange={(e) => updateRow(i, { personal_note: e.target.value.slice(0, 180) })}
                  maxLength={180}
                  rows={1}
                  className="sm:col-span-12 text-sm"
                  data-testid={`invite-composer-note-${i}`}
                />
                {rows.length > 1 && (
                  <button
                    onClick={() => removeRow(i)}
                    className="text-rose-600 hover:text-rose-700 p-1 sm:col-span-12 sm:justify-self-end"
                    data-testid={`invite-composer-remove-${i}`}
                    title="Remove row"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <p className="text-xs text-slate-500">{t("referrals.composer.personalNoteHint")}</p>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={addRow}
                disabled={rows.length >= 5}
                data-testid="invite-composer-add-row-btn"
              >
                <Plus className="w-4 h-4 me-1" />
                {t("referrals.composer.addRow")}
              </Button>
              <Button
                onClick={submit}
                disabled={sending || !rows.some((r) => r.target_email.trim())}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="invite-composer-submit-btn"
              >
                <Send className="w-4 h-4 me-2" />
                {sending ? t("referrals.composer.sending") : t("referrals.composer.submit")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card data-testid="my-referrals-list-card">
          <CardHeader>
            <CardTitle>{t("referrals.list.heading")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {list.length === 0 ? (
              <p className="text-slate-500 text-sm" data-testid="my-referrals-empty">{t("referrals.list.empty")}</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-start text-slate-500 text-xs uppercase border-b border-slate-200">
                    <th className="py-2 pe-3">{t("referrals.list.colTarget")}</th>
                    <th className="py-2 pe-3">{t("referrals.list.colProfile")}</th>
                    <th className="py-2 pe-3">{t("referrals.list.colLang")}</th>
                    <th className="py-2 pe-3">{t("referrals.list.colStatus")}</th>
                    <th className="py-2 pe-3">{t("referrals.list.colSent")}</th>
                    <th className="py-2 pe-3">{t("referrals.list.colLink")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`referral-row-${inv.id}`}>
                      <td className="py-2 pe-3 font-mono text-xs">
                        <Mail className="inline w-3.5 h-3.5 me-1 text-slate-400" />
                        {inv.target_email_masked}
                      </td>
                      <td className="py-2 pe-3">{inv.target_profile_hint || "—"}</td>
                      <td className="py-2 pe-3 uppercase">{inv.target_language_hint || "—"}</td>
                      <td className="py-2 pe-3">
                        <Badge className={statusColor(inv.status)} data-testid={`referral-status-badge-${inv.id}`}>
                          {t(`referrals.status.${inv.status}`)}
                        </Badge>
                      </td>
                      <td className="py-2 pe-3 text-slate-500 text-xs">
                        {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pe-3">
                        {inv.referral_url && (
                          <button
                            onClick={() => copyLink(inv.id, inv.referral_url)}
                            className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 text-xs"
                            data-testid={`referral-copy-${inv.id}`}
                          >
                            {copiedId === inv.id ? (
                              <><Check className="w-3.5 h-3.5" /> {t("referrals.list.copied")}</>
                            ) : (
                              <><Copy className="w-3.5 h-3.5" /> {t("referrals.list.copy")}</>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </PanelLayout>
  );
};

export default MyReferrals;
